import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Trash2, Plus, Loader2, Search } from 'lucide-react';
import workOrderService from '../../services/productionService';
import barStockService from '../../services/barStockService';
import productService from '../../services/productService';
import leftoverService from '../../services/leftoverService';
import inventoryService from '../../services/inventoryService';
import FileAttachmentManager from '../FileAttachmentManager';
import LeftoverIndicator from '../LeftoverIndicator';
import MultiLocationAllocationDialog from '../warehouse/components/MultiLocationAllocationDialog';

// 默认从「丰旗库存」客户库存中出库；如该客户无库存再回退到工单客户/第一个有库存客户
const DEFAULT_SOURCE_CLIENT_ID = '691ad1c3a6d3d2dcc978be1b';

const MaterialIssueDialog = ({
  open,
  onClose,
  workOrder,
  onSuccess,
  mode = 'create',
  existingRequisition = null,
  inline = false,
  onSubmit = null,
  title,
  description
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Global materials list
  const [materials, setMaterials] = useState([]);
  const [warehouses, setWarehouses] = useState(['main']);

  // Remaining bar search state (reserved for offcut selection UI)
  // Using eslint-disable-next-line to reserve for future use
  // eslint-disable-next-line no-unused-vars
  const [remainingBars, setRemainingBars] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [showRemainingSelector, setShowRemainingSelector] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentEditingIndex, setCurrentEditingIndex] = useState(null);

  // Product search modal state
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  const [leftoverSelectorOpen, setLeftoverSelectorOpen] = useState(false);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [notes, setNotes] = useState('');
  const [availableLeftovers, setAvailableLeftovers] = useState([]);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(null);
  const [leftoverSearchLoading, setLeftoverSearchLoading] = useState(false);
  const [selectedLeftover, setSelectedLeftover] = useState(null);
  const [selectedLeftoverQty, setSelectedLeftoverQty] = useState(1);
  
  // Track selected leftovers for BOM materials with leftovers
  const [selectedLeftovers, setSelectedLeftovers] = useState({});
  
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [currentAllocationMaterial, setCurrentAllocationMaterial] = useState(null);
  const [currentAllocationIndex, setCurrentAllocationIndex] = useState(null);

  const loadEditData = useCallback(async () => {
    if (!existingRequisition) return;

    const loadedMaterials = (existingRequisition.products || []).map(product => ({
      material_product_id: product._id || product.product_id,
      material_product_code: product.product_id || product.product_code || '',
      material_name: product.product_name || product.name || '',
      quantity: product.quantity_requested || product.requested_quantity || 1,
      unit: product.unit || '个',
      location: product.location || 'main',
      is_offcut: false,
      offcut_bar_id: null,
      leftover_count: 0,
      leftover_total_quantity: 0,
      stock_by_location: {},
      total_stock: 0,
      frozen_quantity: 0,
      available_quantity: 0,
      frozen_by_location: {},
      available_by_location: {},
      frozen_details: [],
      notes: product.notes || '',
      is_leftover: product.is_leftover || false,
      leftover_id: product.leftover_id || null,
      leftover_code: product.leftover_code || null,
      source_client_id: product.source_client_id || '',
      source_client_name: product.source_client_name || '',
      location_allocations: product.location_allocations || []
    }));

    setMaterials(loadedMaterials);
    setNotes(existingRequisition.remarks || existingRequisition.notes || '');

    // Query stock distribution for each material
    const productCodes = loadedMaterials.map(m => m.material_product_code).filter(Boolean);
    if (productCodes.length > 0) {
      try {
        const stockResponse = await inventoryService.getStockByLocation(
          productCodes,
          existingRequisition.client_id,
          true
        );
        const stockData = stockResponse.stock_by_product || {};

        setMaterials(prev => prev.map(m => {
          const stock = stockData[m.material_product_code];
          if (stock) {
            const locations = Object.entries(stock.locations || {});
            const bestLocation = locations.length > 0
              ? locations.sort((a, b) => b[1] - a[1])[0][0]
              : (m.location || 'main');

            const byClient = stock.by_client || [];
            const reqClientId = existingRequisition.client_id;
            let defaultSourceClient = null;

            if (byClient.length > 0) {
              const defaultClient = byClient.find(
                c => c.client_ref === DEFAULT_SOURCE_CLIENT_ID && (c.quantity || 0) > 0
              );
              if (defaultClient) defaultSourceClient = defaultClient;
            }

            if (!defaultSourceClient && reqClientId && byClient.length > 0) {
              const matchingClient = byClient.find(c => c.client_ref === reqClientId);
              if (matchingClient) defaultSourceClient = matchingClient;
            }

            if (!defaultSourceClient && byClient.length > 0) {
              defaultSourceClient = byClient[0];
            }

            return {
              ...m,
              stock_by_location: stock.locations || {},
              total_stock: stock.total || 0,
              frozen_quantity: stock.frozen_quantity || 0,
              available_quantity: stock.available_quantity !== undefined ? stock.available_quantity : (stock.total || 0),
              frozen_by_location: stock.frozen_by_location || {},
              available_by_location: stock.available_by_location || {},
              frozen_details: stock.frozen_details || [],
              location: m.location || bestLocation,
              by_client: byClient,
              source_client_id: m.source_client_id || defaultSourceClient?.client_ref || '',
              source_client_name: m.source_client_name || defaultSourceClient?.client_name || ''
            };
          }
          return m;
        }));
      } catch (e) {
        console.warn('Failed to fetch stock by location:', e);
      }
    }

    // Check leftover availability
    for (const mat of loadedMaterials) {
      try {
        const match = (mat.material_name || '').match(/(\d+(?:\.\d+)?)\s*mm/);
        const tipDiameter = mat.tip_diameter || (match ? match[1] : null);
        const response = await leftoverService.findAvailable({
          parent_product_id: mat.material_product_id,
          tip_diameter: tipDiameter ? parseFloat(tipDiameter) : undefined,
          min_length: 50
        });
        const leftovers = response.leftovers || [];
        if (leftovers.length > 0) {
          setMaterials(prev => prev.map(m =>
            m.material_product_id === mat.material_product_id
              ? {
                  ...m,
                  leftover_count: leftovers.length,
                  leftover_total_quantity: leftovers.reduce((sum, lo) => sum + (lo.quantity || 1), 0)
                }
              : m
          ));
        }
      } catch (e) {
        // Ignore
      }
    }

    // Load warehouse locations
    const whResponse = await workOrderService.getMaterialWarehouseLocations();
    setWarehouses(whResponse.locations || ['main']);
  }, [existingRequisition]);

  const loadData = useCallback(async () => {
    if (mode === 'edit') {
      await loadEditData();
      return;
    }

    if (!workOrder) return;
    setLoading(true);
    setError(null);
    try {
      // Load BOM materials
      const matsResponse = await workOrderService.getBOMMaterials(workOrder._id);
      
      // Initialize with BOM materials
      const bomMaterials = matsResponse.materials || [];
      const initialMaterials = bomMaterials.map(mat => ({
        material_product_id: mat.material_product_id || mat.product_id,
        material_product_code: mat.material_product_code || mat.product_code || mat.material_product_id,
        material_name: mat.material_name || mat.name,
        quantity: mat.quantity ?? 1,
        unit: mat.unit || '个',
        location: '',
        is_offcut: false,
        offcut_bar_id: null,
        leftover_count: 0,
        leftover_total_quantity: 0,
        stock_by_location: {},
        total_stock: 0
      }));

      setMaterials(initialMaterials);

      // Query stock distribution for each material (across all clients for cross-client requisitions)
      const productIds = initialMaterials.map(m => m.material_product_code).filter(Boolean);
      if (productIds.length > 0) {
        try {
          const stockResponse = await inventoryService.getStockByLocation(
            productIds,
            workOrder.client_id,
            true // include_all_clients
          );
          const stockData = stockResponse.stock_by_product || {};

          setMaterials(prev => prev.map(m => {
            const stock = stockData[m.material_product_code];
            if (stock) {
              // Pick location with highest stock as default
              const locations = Object.entries(stock.locations || {});
              const bestLocation = locations.length > 0
                ? locations.sort((a, b) => b[1] - a[1])[0][0]
                : 'main';

              // Get by_client data for cross-client selection
              const byClient = stock.by_client || [];

              // 1) 优先使用默认客户「丰旗库存」（只要有库存）
              // 2) 其次匹配工单客户
              // 3) 最后回退到第一个有库存的客户
              const workOrderClientId = workOrder.client_id;
              let defaultSourceClient = null;

              if (byClient.length > 0) {
                const defaultClient = byClient.find(
                  c => c.client_ref === DEFAULT_SOURCE_CLIENT_ID && (c.quantity || 0) > 0
                );
                if (defaultClient) {
                  defaultSourceClient = defaultClient;
                }
              }

              if (!defaultSourceClient && workOrderClientId && byClient.length > 0) {
                const matchingClient = byClient.find(c => c.client_ref === workOrderClientId);
                if (matchingClient) {
                  defaultSourceClient = matchingClient;
                }
              }

              if (!defaultSourceClient && byClient.length > 0) {
                defaultSourceClient = byClient[0];
              }

              return {
                ...m,
                stock_by_location: stock.locations || {},
                total_stock: stock.total || 0,
                frozen_quantity: stock.frozen_quantity || 0,
                available_quantity: stock.available_quantity !== undefined ? stock.available_quantity : (stock.total || 0),
                frozen_by_location: stock.frozen_by_location || {},
                available_by_location: stock.available_by_location || {},
                frozen_details: stock.frozen_details || [],
                location: bestLocation,
                by_client: byClient,
                source_client_id: defaultSourceClient?.client_ref || '',
                source_client_name: defaultSourceClient?.client_name || ''
              };
            }
            return m;
          }));
        } catch (e) {
          console.warn('Failed to fetch stock by location:', e);
        }
      }

      // Check leftover availability for each material
      for (const mat of initialMaterials) {
        try {
          const match = (mat.material_name || '').match(/(\d+(?:\.\d+)?)\s*mm/);
          const tipDiameter = mat.tip_diameter || (match ? match[1] : null);
          const response = await leftoverService.findAvailable({
            parent_product_id: mat.material_product_id,
            tip_diameter: tipDiameter ? parseFloat(tipDiameter) : undefined,
            min_length: 50
          });
          const leftovers = response.leftovers || [];
          if (leftovers.length > 0) {
            setMaterials(prev => prev.map(m =>
              m.material_product_id === mat.material_product_id
                ? {
                    ...m,
                    leftover_count: leftovers.length,
                    leftover_total_quantity: leftovers.reduce((sum, lo) => sum + (lo.quantity || 1), 0)
                  }
                : m
            ));
          }
        } catch (e) {
          // Ignore errors for individual materials
        }
      }

      // Load warehouse locations for materials only
      const whResponse = await workOrderService.getMaterialWarehouseLocations();
      const locations = whResponse.locations || ['main'];
      setWarehouses(locations);

      // Apply calculated material suggestions
      try {
        const suggestionResponse = await workOrderService.suggestMaterials(workOrder._id);
        if (suggestionResponse.success && suggestionResponse.suggestions && suggestionResponse.suggestions.length > 0) {
          setMaterials(prev => {
            const updated = [...prev];
            suggestionResponse.suggestions.forEach(suggestion => {
              const idx = updated.findIndex(m =>
                m.material_product_code === suggestion.material_product_code ||
                m.material_product_id === suggestion.material_product_id
              );

              if (idx >= 0) {
                updated[idx] = {
                  ...updated[idx],
                  quantity: suggestion.recommended_quantity ?? updated[idx].quantity,
                  unit: suggestion.unit || updated[idx].unit
                };
              }
            });
            return updated;
          });
        }
      } catch (e) {
        console.warn('Failed to load material suggestions:', e);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [workOrder, loadEditData, mode]);

  // Load data when dialog opens
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && existingRequisition) {
      loadData();
    } else if (mode === 'create' && workOrder) {
      loadData();
    }
  }, [open, workOrder, existingRequisition, mode, loadData]);

  // Search remaining bars by tip_diameter
  const searchRemainingBars = async (tipDiameter, index) => {
    if (!tipDiameter) {
      setRemainingBars([]);
      return;
    }
    
    try {
      const bars = await barStockService.getRemainingBars(tipDiameter);
      setRemainingBars(bars || []);
      setCurrentEditingIndex(index);
      setShowRemainingSelector(true);
    } catch (err) {
      console.error('Failed to fetch remaining bars:', err);
      setRemainingBars([]);
    }
  };

  const searchLeftovers = async (material, index) => {
    if (!material.material_product_id) {
      setError('请先选择物料');
      return;
    }
    
    setLeftoverSearchLoading(true);
    setSelectedMaterialIndex(index);
    setError(null);
    
    try {
      const match = (material.material_name || '').match(/(\d+(?:\.\d+)?)\s*mm/);
      const tipDiameter = material.tip_diameter || (match ? match[1] : null);
      
      const response = await leftoverService.findAvailable({
        parent_product_id: material.material_product_id,
        tip_diameter: tipDiameter ? parseFloat(tipDiameter) : undefined,
        min_length: 50
      });
      
      // Filter out already selected or added leftovers
      const allLeftovers = response.leftovers || [];
      const selectedLeftoverIds = Object.values(selectedLeftovers).map(lo => lo._id);
      const addedLeftoverIds = materials.filter(m => m.leftover_id).map(m => m.leftover_id);
      const usedLeftoverIds = [...selectedLeftoverIds, ...addedLeftoverIds];
      const filteredLeftovers = allLeftovers.filter(lo => !usedLeftoverIds.includes(lo._id));
      
      setAvailableLeftovers(filteredLeftovers);
      setLeftoverSelectorOpen(true);
    } catch (err) {
      console.error('Failed to fetch leftovers:', err);
      setError('获取余料列表失败');
    } finally {
      setLeftoverSearchLoading(false);
    }
  };

  const handleSelectLeftover = (leftover, quantity = 1) => {
    setMaterials(prev => [
      ...prev,
      {
        material_product_id: leftover.parent_product_id,
        material_product_code: leftover.parent_product_code || '',
        material_name: leftover.parent_product_name || '',
        quantity: quantity,
        unit: '根',
        location: leftover.location || 'main',
        is_leftover: true,
        leftover_id: leftover._id,
        leftover_code: leftover.code,
        tip_diameter: leftover.tip_diameter,
        length: leftover.length
      }
    ]);
    
    setLeftoverSelectorOpen(false);
    setAvailableLeftovers([]);
  };
  
  // Handle selecting leftover for BOM material
  const handleBOMLeftoverSelect = (materialId, leftover) => {
    setSelectedLeftovers(prev => ({
      ...prev,
      [materialId]: leftover
    }));
    setMaterials(prev => prev.map(m =>
      m.material_product_id === materialId
        ? {
            ...m,
            is_leftover: true,
            leftover_id: leftover._id,
            leftover_code: leftover.code,
            location: leftover.location || m.location
          }
        : m
    ));
  };

  // Add new material row (now opens product search)
  const addMaterialRow = () => {
    setProductSearchTerm('');
    setProductSearchResults([]);
    setProductSearchOpen(true);
  };

  // Search products for material selection
  const searchProducts = useCallback(async () => {
    if (!productSearchTerm.trim()) {
      setProductSearchResults([]);
      return;
    }

    try {
      setProductSearchLoading(true);
      const response = await productService.searchProducts(
        productSearchTerm,
        1,
        20,
        'raw_material'
      );
      setProductSearchResults(response.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
      setProductSearchResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, [productSearchTerm]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchOpen && productSearchTerm) {
        searchProducts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm, productSearchOpen, searchProducts]);

  // Handle product selection from search modal
  const handleSelectProduct = async (product) => {
    const productCode = product.product_id || product.product_code;
    
    // Fetch stock by location for the selected product
    let stockByLocation = {};
    let totalStock = 0;
    let defaultLocation = warehouses[0] || 'main';
    let byClient = [];
    let sourceClientId = '';
    let sourceClientName = '';
    let frozenQty = 0;
    let availableQty = 0;
    let frozenByLoc = {};
    let availableByLoc = {};
    let frozenDetails = [];
    
    try {
      const stockResponse = await inventoryService.getStockByLocation(
        [productCode],
        workOrder?.client_id,
        true // include_all_clients
      );
      const stockData = stockResponse.stock_by_product || {};
      const stock = stockData[productCode];
      
      if (stock) {
        stockByLocation = stock.locations || {};
        totalStock = stock.total || 0;
        byClient = stock.by_client || [];
        frozenQty = stock.frozen_quantity || 0;
        availableQty = stock.available_quantity !== undefined ? stock.available_quantity : totalStock;
        frozenByLoc = stock.frozen_by_location || {};
        availableByLoc = stock.available_by_location || {};
        frozenDetails = stock.frozen_details || [];
        
        // Pick location with highest stock as default
        const locations = Object.entries(stockByLocation);
        if (locations.length > 0) {
          defaultLocation = locations.sort((a, b) => b[1] - a[1])[0][0];
        }
        
        // Determine source client: 优先「丰旗库存」> 工单客户 > 第一个有库存客户
        const workOrderClientId = workOrder?.client_id;
        if (byClient.length > 0) {
          const defaultClient = byClient.find(
            c => c.client_ref === DEFAULT_SOURCE_CLIENT_ID && (c.quantity || 0) > 0
          );
          if (defaultClient) {
            sourceClientId = defaultClient.client_ref;
            sourceClientName = defaultClient.client_name;
          }
        }
        if (!sourceClientId && workOrderClientId && byClient.length > 0) {
          const matchingClient = byClient.find(c => c.client_ref === workOrderClientId);
          if (matchingClient) {
            sourceClientId = matchingClient.client_ref;
            sourceClientName = matchingClient.client_name;
          }
        }
        if (!sourceClientId && byClient.length > 0) {
          sourceClientId = byClient[0].client_ref;
          sourceClientName = byClient[0].client_name;
        }
      }
    } catch (e) {
      console.warn('Failed to fetch stock by location for selected product:', e);
    }
    
    setMaterials(prev => [...prev, {
      material_product_id: product._id,
      material_product_code: productCode,
      material_name: product.name,
      quantity: 1,
      unit: product.unit || '个',
      location: defaultLocation,
      is_offcut: false,
      offcut_bar_id: null,
      stock_by_location: stockByLocation,
      total_stock: totalStock,
      frozen_quantity: frozenQty,
      available_quantity: availableQty,
      frozen_by_location: frozenByLoc,
      available_by_location: availableByLoc,
      frozen_details: frozenDetails,
      by_client: byClient,
      source_client_id: sourceClientId,
      source_client_name: sourceClientName
    }]);
    setProductSearchOpen(false);
  };

  // Remove material row
  const removeMaterialRow = (index) => {
    setMaterials(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleOpenAllocation = (material, index) => {
    setCurrentAllocationMaterial(material);
    setCurrentAllocationIndex(index);
    setAllocationDialogOpen(true);
  };

  const handleAllocationConfirm = (allocationResult) => {
    if (currentAllocationIndex === null) return;
    
    setMaterials(prev => prev.map((m, idx) => {
      if (idx === currentAllocationIndex) {
        return {
          ...m,
          location_allocations: allocationResult.locationAllocations || [],
          location: allocationResult.locationAllocations?.[0]?.location || m.location,
          is_multi_location: (allocationResult.locationAllocations?.length || 0) > 1
        };
      }
      return m;
    }));
    
    setAllocationDialogOpen(false);
    setCurrentAllocationMaterial(null);
    setCurrentAllocationIndex(null);
  };

  // Update material field
  const updateMaterial = (index, field, value) => {
    setMaterials(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // If changing to offcut, search for remaining bars
      if (field === 'is_offcut' && value === true) {
        const match = (updated[index].material_name || '').match(/(\d+(?:\.\d+)?)\s*mm/);
        const tipDiameter = updated[index].tip_diameter || (match ? match[1] : null);
        if (tipDiameter) {
          searchRemainingBars(parseFloat(tipDiameter), index);
        }
      }

      if (field === 'is_leftover' && value === false) {
        const materialId = updated[index].material_product_id;
        setSelectedLeftovers(prev => {
          const newState = { ...prev };
          delete newState[materialId];
          return newState;
        });
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    // Validate
    const invalidMaterials = materials.filter(m => !m.material_product_code || !m.location);
    if (invalidMaterials.length > 0) {
      setError('请填写所有物料的产品编码和仓库位置');
      return;
    }

    // Check available quantity against frozen stock
    const insufficientMaterials = materials.filter(m =>
      m.available_quantity !== undefined && m.quantity > m.available_quantity
    );
    if (insufficientMaterials.length > 0) {
      const names = insufficientMaterials.map(m => m.material_name || m.material_product_code).join('、');
      setError(`以下物料可用数量不足（已扣除冻结）：${names}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Prepare materials payload with leftover and cross-client information
      const materialsPayload = materials.map(mat => {
        const selectedLeftover = selectedLeftovers[mat.material_product_id];
        const isLeftover = mat.is_leftover || !!selectedLeftover;
        const leftoverId = mat.leftover_id || selectedLeftover?._id || null;
        const leftoverCode = mat.leftover_code || selectedLeftover?.code || null;
        
        const payload = {
          material_product_id: mat.material_product_id || mat.material_product_code,
          material_product_code: mat.material_product_code,
          material_name: mat.material_name,
          quantity: parseFloat(mat.quantity) || 0,
          unit: mat.unit,
          location: mat.location,
          is_leftover: isLeftover,
          leftover_id: leftoverId,
          leftover_code: leftoverCode,
          source_client_id: mat.source_client_id || null,
          source_client_name: mat.source_client_name || ''
        };
        
        
        if (mat.location_allocations && mat.location_allocations.length > 0) {
          payload.location_allocations = mat.location_allocations.map(alloc => ({
            location: alloc.location,
            quantity: parseFloat(alloc.quantity) || 0,
            source_client_id: alloc.source_client_id || mat.source_client_id || null,
            source_client_name: alloc.source_client_name || mat.source_client_name || ''
          }));
        }
        
        return payload;
      });

      // Get attachment IDs
      const attachmentIds = attachments.map(att => att._id || att.id).filter(Boolean);

      if (mode === 'edit') {
        // Convert editor materials to update API format
        const updatePayload = {
          products: materials.map(mat => ({
            _id: mat.material_product_id || mat.material_product_code,
            product_id: mat.material_product_code,
            quantity_requested: parseFloat(mat.quantity) || 0,
            unit: mat.unit,
            notes: mat.notes || ''
          })),
          notes
        };

        let result = null;
        if (onSubmit) {
          result = await onSubmit(updatePayload);
          if (result && result.status === 'pending_approval') {
            alert(`领料申请已更新：${result.requisition_id || result.requisition_number || ''}`);
          }
        }
        if (onSuccess) onSuccess(result);
        handleClose();
        return;
      }

      const result = await workOrderService.addMaterialIssue(
        workOrder._id,
        {
          materials: materialsPayload,
          notes: notes,
          attachments: attachmentIds
        }
      );

      // Show success message with approval pending notice
      if (result && result.status === 'pending_approval') {
        setError(null);
        alert(`领料申请已创建：${result.requisition_id}\n请等待审批通过后物料才会出库。`);
      }

      if (onSuccess) onSuccess(result);
      handleClose();
    } catch (err) {
      console.error(`Failed to ${mode === 'edit' ? 'update' : 'add'} material issue:`, err);
      const errorMsg = (err.response && err.response.data && err.response.data.error) ||
                       (err.response && err.response.data && err.response.data.message) ||
                       (mode === 'edit' ? '更新领料申请失败' : '添加领料失败');
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMaterials([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title || (mode === 'edit' ? '编辑领料申请' : '申请领料')}</DialogTitle>
          <DialogDescription>
            {description || (mode === 'edit'
              ? `编辑领料申请 ${(existingRequisition && existingRequisition.requisition_number) || existingRequisition?.requisition_id || ''}`
              : `为工单 ${(workOrder && workOrder.production_order_id) || '-'} 创建领料申请，等待审批`)}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Materials Table */}
            <div className="border rounded-md">
              <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 font-medium text-sm border-b">
                <div className="col-span-3">物料编码</div>
                <div className="col-span-3">物料名称</div>
                <div className="col-span-2">数量</div>
                <div className="col-span-2">仓库位置</div>
                <div className="col-span-2">操作</div>
              </div>
              
              <div className="divide-y">
                {materials.map((mat, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 p-3 items-center border-b last:border-b-0">
                    <div className="col-span-3">
                      <Input
                        value={mat.material_product_code}
                        onChange={(e) => updateMaterial(idx, 'material_product_code', e.target.value)}
                        placeholder="物料编码"
                        className="h-8"
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        value={mat.material_name}
                        onChange={(e) => updateMaterial(idx, 'material_name', e.target.value)}
                        placeholder="物料名称"
                        className="h-8"
                      />
                    </div>
                    <div className="col-span-2 flex gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={mat.quantity}
                        onChange={(e) => updateMaterial(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="h-8 w-20"
                      />
                      <span className="text-sm text-muted-foreground self-center">{mat.unit}</span>
                    </div>
                    <div className="col-span-2">
                      {mat.location_allocations?.length > 1 ? (
                        <div className="space-y-1">
                          {mat.location_allocations.map((alloc, i) => (
                            <div key={i} className="text-xs flex justify-between items-center">
                              <span className="font-medium">{alloc.location}:</span>
                              <span>{alloc.quantity}{mat.unit}</span>
                            </div>
                          ))}
                          <div className="text-xs text-muted-foreground border-t pt-1 flex justify-between">
                            <span>总计:</span>
                            <span>{mat.quantity}{mat.unit}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-1 text-xs"
                            onClick={() => handleOpenAllocation(mat, idx)}
                          >
                            修改分配
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <select
                            value={mat.location}
                            onChange={(e) => updateMaterial(idx, 'location', e.target.value)}
                            className="w-full px-2 py-1 border rounded h-8 bg-background"
                          >
                            {Object.entries(mat.stock_by_location || {}).map(([loc, qty]) => {
                              const available = (mat.available_by_location && mat.available_by_location[loc]) !== undefined
                                ? mat.available_by_location[loc]
                                : qty;
                              return (
                                <option key={loc} value={loc} style={{ color: available <= 0 ? '#ef4444' : 'inherit' }}>
                                  {loc === 'main' ? '主仓库' : loc} ({available}件可用)
                                </option>
                              );
                            })}
                            {Object.keys(mat.stock_by_location || {}).length === 0 && warehouses.map((loc, lidx) => (
                              <option key={lidx} value={loc}>
                                {loc === 'main' ? '主仓库' : loc}
                              </option>
                            ))}
                          </select>
                          {Object.keys(mat.stock_by_location || {}).length > 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full text-xs"
                              onClick={() => handleOpenAllocation(mat, idx)}
                            >
                              多库位分配
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        总库存: {mat.total_stock ?? 0}件
                        <span
                          className={`ml-1 cursor-help ${mat.frozen_quantity > 0 ? 'text-orange-500' : 'text-muted-foreground'}`}
                          title={mat.frozen_details?.length > 0
                            ? mat.frozen_details.map(d => `工单 ${d.work_order_number || '-'}: ${d.quantity}件`).join('\n')
                            : `冻结: ${mat.frozen_quantity ?? 0}件`
                          }
                        >
                          (冻结: {mat.frozen_quantity ?? 0}件)
                        </span>
                      </div>
                      <div className={`text-xs mt-1 ${(mat.available_quantity ?? mat.total_stock ?? 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                        可用: {mat.available_quantity ?? mat.total_stock ?? 0}件
                      </div>
                      {(mat.total_stock ?? 0) <= 0 && (
                        <div className="text-xs text-red-500 mt-1">
                          ⚠️ 库存不足
                        </div>
                      )}
                      {(mat.available_quantity ?? mat.total_stock ?? 0) <= 0 && (mat.total_stock ?? 0) > 0 && (
                        <div className="text-xs text-orange-500 mt-1">
                          ⚠️ 可用数量不足（已扣除冻结）
                        </div>
                      )}
                      {(mat.by_client?.length > 1 || (mat.source_client_id && mat.by_client?.length > 0)) && (
                        <select
                          value={mat.source_client_id || ''}
                          onChange={(e) => {
                            const selected = mat.by_client.find(c => c.client_ref === e.target.value);
                            updateMaterial(idx, 'source_client_id', e.target.value);
                            updateMaterial(idx, 'source_client_name', selected?.client_name || '');
                          }}
                          className="w-full px-2 py-1 border rounded h-8 bg-background mt-1 text-xs"
                        >
                          {mat.by_client.filter(c => c.quantity > 0).map((client) => (
                            <option key={client.client_ref} value={client.client_ref}>
                              {client.client_name} ({client.quantity}件)
                            </option>
                          ))}
                        </select>
                      )}
                      {mat.source_client_name && (
                        <div className="text-xs text-blue-600 mt-1">
                          来源: {mat.source_client_name}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => searchLeftovers(mat, idx)}
                        disabled={leftoverSearchLoading && selectedMaterialIndex === idx}
                        className={mat.is_leftover ? 'bg-primary/10 text-primary' : ''}
                      >
                        {leftoverSearchLoading && selectedMaterialIndex === idx ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : mat.is_leftover ? (
                          mat.leftover_code || '余料'
                        ) : (
                          '🔍 余料'
                        )}
                      </Button>
                      {mat.is_leftover && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateMaterial(idx, 'is_leftover', false)}
                          className="text-muted-foreground"
                        >
                          取消
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMaterialRow(idx)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {/* Leftover Indicator Row */}
                    {mat.leftover_count > 0 && (
                      <div className="col-span-12 mt-2 pt-2 border-t">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                            有余料可用
                          </Badge>
                          <LeftoverIndicator
                            productId={mat.material_product_id}
                            leftoverCount={mat.leftover_count}
                            leftoverTotalQuantity={mat.leftover_total_quantity}
                            onLeftoverSelect={(leftover) => handleBOMLeftoverSelect(mat.material_product_id, leftover)}
                            excludeLeftoverIds={[
                              ...Object.values(selectedLeftovers).map(lo => lo._id),
                              ...materials.filter(m => m.leftover_id).map(m => m.leftover_id)
                            ]}
                          />
                          {selectedLeftovers[mat.material_product_id] && (
                            <Badge variant="secondary" className="text-green-600">
                              已选择：{selectedLeftovers[mat.material_product_id].code}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Add Material Button */}
            <Button
              variant="outline"
              onClick={addMaterialRow}
              className="w-full gap-2"
            >
              <Plus className="h-4 w-4" />
              添加物料
            </Button>

            {/* Attachments */}
            <div className="space-y-2">
              <label className="text-sm font-medium">附件</label>
              <FileAttachmentManager
                entityType="material_requisitions"
                entityId={null}
                files={attachments}
                onChange={setAttachments}
                maxFiles={5}
                maxSizeMB={10}
                showSearch={false}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">备注</label>
              <textarea
                className="w-full p-2 border rounded-md"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="填写申请备注信息..."
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                提交中...
              </>
            ) : (
              mode === 'edit' ? '保存修改' : '申请领料'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Product Search Dialog */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-2xl z-[100]">
          <DialogHeader>
            <DialogTitle>选择物料</DialogTitle>
            <DialogDescription>
              从产品目录中搜索并选择原材料。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按产品编号或名称搜索..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {productSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : productSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {productSearchTerm ? '未找到产品。' : '输入搜索词查找产品。'}
                </div>
              ) : (
                <div className="border rounded-md">
                  <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 font-medium text-sm border-b">
                    <div className="col-span-4">产品编号</div>
                    <div className="col-span-6">名称</div>
                    <div className="col-span-2">单位</div>
                  </div>
                  <div className="divide-y max-h-48 overflow-y-auto">
                    {productSearchResults.map((product) => (
                      <div
                        key={product._id}
                        className="grid grid-cols-12 gap-2 p-3 cursor-pointer hover:bg-muted"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className="col-span-4 font-mono text-sm">{product.product_id || product.product_code}</div>
                        <div className="col-span-6">{product.name}</div>
                        <div className="col-span-2 text-sm text-muted-foreground">{product.unit || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={leftoverSelectorOpen} onOpenChange={(open) => {
        setLeftoverSelectorOpen(open);
        if (!open) {
          setSelectedLeftover(null);
          setSelectedLeftoverQty(1);
        }
      }}>
        <DialogContent className="max-w-2xl z-[100]">
          <DialogHeader>
            <DialogTitle>选择余料</DialogTitle>
            <DialogDescription>
              选择可用的余料用于此物料
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {availableLeftovers.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-muted-foreground mb-2">暂无可用余料</div>
                <div className="text-xs text-muted-foreground">
                  提示：余料在完成工单时自动创建。请先完成一个工单并填写余料信息。
                </div>
              </div>
            ) : (
              <div className="border rounded-md">
                <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 font-medium text-sm border-b">
                  <div className="col-span-2">余料编号</div>
                  <div className="col-span-2">类型</div>
                  <div className="col-span-3">规格</div>
                  <div className="col-span-2">库位</div>
                  <div className="col-span-1">数量</div>
                  <div className="col-span-2">操作</div>
                </div>
                <div className="divide-y max-h-64 overflow-y-auto">
                  {availableLeftovers.map((leftover) => (
                    <div
                      key={leftover._id}
                      className={`grid grid-cols-12 gap-2 p-3 items-center ${(selectedLeftover && selectedLeftover._id) === leftover._id ? 'bg-primary/10' : ''}`}
                    >
                      <div className="col-span-2 font-mono text-sm">{leftover.code}</div>
                      <div className="col-span-2">
                        <Badge variant={leftover.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-xs">
                          {leftover.leftover_type === 'ground' ? '精磨' : '毛坯'}
                        </Badge>
                        {(leftover.source?.type === 'subcontract_return' || leftover.source?.type === '外协回料') && (
                          <span className="text-[10px] text-muted-foreground block">外协</span>
                        )}
                      </div>
                      <div className="col-span-3 text-sm">
                        <div className="font-medium">{leftover.parent_product_name}</div>
                        <div className="text-muted-foreground text-xs">
                          直径: {leftover.tip_diameter}mm × 长度: {leftover.length}mm
                        </div>
                      </div>
                      <div className="col-span-2 text-sm text-muted-foreground">{leftover.location}</div>
                      <div className="col-span-1">
                        <span className="font-medium text-green-600">{leftover.quantity || 1}</span>
                      </div>
                      <div className="col-span-2">
                        {(selectedLeftover && selectedLeftover._id) === leftover._id ? (
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              min={1}
                              max={leftover.quantity || 1}
                              value={selectedLeftoverQty}
                              onChange={(e) => setSelectedLeftoverQty(Math.min(Math.max(1, parseInt(e.target.value) || 1), leftover.quantity || 1))}
                              className="h-8 w-16"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleSelectLeftover(leftover, selectedLeftoverQty)}
                              className="flex-1"
                            >
                              确认
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedLeftover(leftover);
                              setSelectedLeftoverQty(1);
                            }}
                            className="w-full"
                          >
                            选择
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLeftoverSelectorOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MultiLocationAllocationDialog
        open={allocationDialogOpen}
        onClose={() => {
          setAllocationDialogOpen(false);
          setCurrentAllocationMaterial(null);
          setCurrentAllocationIndex(null);
        }}
        product={currentAllocationMaterial ? {
          product_code: currentAllocationMaterial.material_product_code,
          product_id: currentAllocationMaterial.material_product_id,
          product_name: currentAllocationMaterial.material_name,
          quantity_actual: currentAllocationMaterial.quantity,
          quantity_planned: currentAllocationMaterial.quantity,
          unit: currentAllocationMaterial.unit,
          locationAllocations: currentAllocationMaterial.location_allocations
        } : null}
        onConfirm={handleAllocationConfirm}
      />
    </Dialog>
  );
};

export default MaterialIssueDialog;
